// Phase 35e — Investor Letters section on PartnershipDetail (sponsor view).
// Phase 35.1 polish — lifted preview state so the modal can be re-opened
// for any past draft via a "View" eye button on each row.
//
// Lists previously-generated draft letters (newest-first) and exposes a
// "Generate new letter" button that opens a modal with period dropdown
// + year + custom notes textarea. After generation, the preview pane
// shows the letter HTML in a readable card the sponsor can hand-edit
// before copying to their email client. Past drafts can be re-previewed
// without re-running the LLM via the per-row eye button.
//
// No auto-send. No DocuSign. By design: this phase ships the draft +
// preview + copy-to-clipboard surface only.

import { useCallback, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Mail, FileText, Loader2, Copy, Sparkles, AlertTriangle, Eye, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Phase 36 Batch 1 — XSS hardening. Allowlist of tags + attributes that
// MAY appear in the LLM-composed letter HTML before we hand it to
// `dangerouslySetInnerHTML`. Anything outside this list is stripped by
// DOMPurify, including `<script>`, inline `on*` event handlers, `<iframe>`,
// `<form>`, `<object>`, `javascript:` URLs, etc.
//
// The letter content is structured prose / a memo; it never needs
// scripts, forms, or media tags. Keep this list narrow.
const LETTER_SANITIZE_OPTS = {
  ALLOWED_TAGS: [
    "p", "br", "div", "span",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "strong", "em", "b", "i", "u",
    "ul", "ol", "li",
    "blockquote", "hr",
    "table", "thead", "tbody", "tr", "th", "td",
    "a",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "class", "style"],
  ALLOWED_URI_REGEXP: /^(https?:|mailto:|#)/i,
};

const PERIODS = ["Q1", "Q2", "Q3", "Q4"];

function CurrentQuarter() {
  const m = new Date().getMonth() + 1;
  return m <= 3 ? "Q1" : m <= 6 ? "Q2" : m <= 9 ? "Q3" : "Q4";
}

export default function InvestorLettersSection({ partnershipId }) {
  const [drafts, setDrafts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  // Phase 35.1 — when set, the modal opens directly in "preview" mode
  // showing the existing draft (no LLM re-roll).
  const [seedPreview, setSeedPreview] = useState(null);

  const fetchDrafts = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/syndication/partnerships/${partnershipId}/investor-letters`,
      );
      setDrafts(data?.rows || []);
    } catch {
      setDrafts([]);
    }
  }, [partnershipId]);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  const openForView = (draft) => {
    setSeedPreview({
      subject_line: draft.subject_line,
      letter_plain: draft.letter_plain,
      letter_html: draft.letter_html,
      source: draft.source,
      generated_at: draft.generated_at,
      period: draft.period,
      year: draft.year,
    });
    setModalOpen(true);
  };

  return (
    <section data-testid="synd-letter-section" className="mt-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            📧 Investor Letters
          </h2>
          <p className="text-xs text-slate-500">
            LLM-composed quarterly LP updates. You edit + send.
          </p>
        </div>
        <Button
          onClick={() => { setSeedPreview(null); setModalOpen(true); }}
          data-testid="synd-letter-generate-btn"
          className="bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
        >
          <Sparkles className="me-1.5 h-4 w-4" />
          Generate new letter
        </Button>
      </div>

      {drafts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white/60 p-6 text-center text-sm text-slate-500">
          No drafts yet. Click "Generate new letter" to compose one.
        </div>
      ) : (
        <ul className="space-y-2">
          {drafts.map((d) => (
            <li
              key={d.id}
              className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-all hover:border-indigo-200"
              data-testid={`synd-letter-draft-${d.id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">
                  {d.subject_line}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {(d.generated_at || "").slice(0, 10)} • {d.period} {d.year} •{" "}
                  {d.source === "llm_phrased" ? (
                    <span className="text-indigo-600">AI-composed</span>
                  ) : (
                    <span className="text-amber-700">Template fallback</span>
                  )}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openForView(d)}
                  data-testid={`synd-letter-view-${d.id}`}
                  className="border-slate-200 text-slate-700"
                  title="Preview this draft"
                >
                  <Eye className="me-1 h-3.5 w-3.5" />
                  View
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard?.writeText(d.letter_plain || "");
                    toast.success("Letter copied to clipboard.");
                  }}
                  data-testid={`synd-letter-copy-row-${d.id}`}
                  className="border-slate-200 text-slate-700"
                >
                  <Copy className="me-1 h-3.5 w-3.5" />
                  Copy
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {modalOpen && (
        <GenerateLetterModal
          partnershipId={partnershipId}
          initialPreview={seedPreview}
          onClose={() => { setModalOpen(false); setSeedPreview(null); }}
          onGenerated={(payload) => {
            setDrafts((d) => [payload, ...d]);
            fetchDrafts();
          }}
        />
      )}
    </section>
  );
}


function GenerateLetterModal({ partnershipId, initialPreview, onClose, onGenerated }) {
  const [period, setPeriod] = useState(initialPreview?.period || CurrentQuarter());
  const [year, setYear] = useState(initialPreview?.year || new Date().getFullYear());
  const [customNotes, setCustomNotes] = useState("");
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(initialPreview || null);
  const [error, setError] = useState(null);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const { data } = await api.post(
        `/syndication/partnerships/${partnershipId}/investor-letter`,
        { period, year: Number(year), custom_notes: customNotes || null },
      );
      setPreview(data);
      onGenerated?.({
        id: `il-${partnershipId}-${period}-${year}-${Date.now()}`,
        subject_line: data.subject_line,
        letter_plain: data.letter_plain,
        letter_html: data.letter_html,
        period,
        year,
        source: data.source,
        generated_at: data.generated_at,
      });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403) {
        setError(detail || "Only the partnership sponsor can generate investor letters.");
      } else {
        setError(detail || e.message || "Could not generate letter.");
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="synd-letter-modal"
        className="max-w-3xl"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-indigo-600" />
            {preview ? "LP Letter Preview" : "Compose Q LP Letter"}
          </DialogTitle>
        </DialogHeader>
        {!preview ? (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  Period
                </label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger data-testid="synd-letter-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIODS.map((q) => (
                      <SelectItem key={q} value={q}>{q}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  Year
                </label>
                <Input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  data-testid="synd-letter-year-input"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                Notes for the assistant (optional)
              </label>
              <Textarea
                value={customNotes}
                onChange={(e) => setCustomNotes(e.target.value)}
                placeholder="e.g. Renovations on track; new tenant signed for unit 2B."
                rows={4}
                data-testid="synd-letter-notes-input"
              />
              <p className="mt-1 text-[11px] text-slate-500">
                These notes are passed to the LLM. Leave blank for a numbers-only letter.
              </p>
            </div>
            {error && (
              <p className="flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
                <AlertTriangle className="h-3.5 w-3.5" /> {error}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                Suggested subject
              </p>
              <p
                className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-800"
                data-testid="synd-letter-preview-subject"
              >
                {preview.subject_line}
              </p>
            </div>
            <div>
              <p className="mb-1 flex items-center justify-between">
                <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  Letter preview (editable)
                </span>
                {preview.source && (
                  <span className={`text-[10px] ${
                    preview.source === "llm_phrased"
                      ? "text-indigo-600"
                      : "text-amber-700"
                  }`}>
                    {preview.source === "llm_phrased" ? "AI-composed" : "Template fallback"}
                  </span>
                )}
              </p>
              <div
                contentEditable
                suppressContentEditableWarning
                data-testid="synd-letter-preview"
                className="min-h-[200px] max-h-[400px] overflow-y-auto rounded-lg border border-slate-200 bg-white p-3 text-sm leading-relaxed text-slate-800"
                dangerouslySetInnerHTML={{
                  // Phase 36 Batch 1 — sanitized at render time. See
                  // LETTER_SANITIZE_OPTS at the top of this file.
                  __html: DOMPurify.sanitize(preview.letter_html || "", LETTER_SANITIZE_OPTS),
                }}
              />
            </div>
          </div>
        )}
        <DialogFooter>
          {!preview ? (
            <>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={generating}
                data-testid="synd-letter-generate-confirm"
                className="bg-indigo-600 text-white hover:bg-indigo-700"
              >
                {generating ? (
                  <>
                    <Loader2 className="me-1.5 h-4 w-4 animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <FileText className="me-1.5 h-4 w-4" />
                    Generate
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard?.writeText(preview.letter_plain || "");
                  toast.success("Letter copied to clipboard.");
                }}
                data-testid="synd-letter-copy-btn"
                className="border-slate-200 text-slate-700"
              >
                <Copy className="me-1.5 h-4 w-4" />
                Copy to clipboard
              </Button>
              <Button
                onClick={onClose}
                data-testid="synd-letter-close-btn"
                className="bg-indigo-600 text-white hover:bg-indigo-700"
              >
                <X className="me-1.5 h-4 w-4" />
                Close
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
